import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BookOpen,
  CheckCircle2,
  ClipboardList,
  Copy,
  FlaskConical,
  HelpCircle,
  Loader2,
  Sparkles,
  Users,
} from 'lucide-react';
import CarePageHeader from '@/components/CarePageHeader';
import { Button } from '@/components/ui/button';
import {
  GOALS,
  LINES_OF_CARE,
  PERFORMANCE,
  PRIOR_MODALITIES,
  REGIONS,
  STAGES,
  buildClinicBriefText,
  fetchCatalog,
  fetchSuggestedBiomarkers,
  personalizeTreatmentOptions,
  prefillFromProfile,
} from '@/lib/treatmentOptions';
import '@/components/care-page.css';
import './treatment-options.css';

const EMPTY_RESULT = null;

export default function TreatmentOptionsPage() {
  const prefill = useMemo(() => prefillFromProfile(), []);
  const [cancerType, setCancerType] = useState(prefill?.cancerType || 'Breast Cancer');
  const [stage, setStage] = useState(prefill?.stage || 'II');
  const [goals, setGoals] = useState(['second_look', 'cure_intent']);
  const [lineOfCare, setLineOfCare] = useState('newly_diagnosed');
  const [region, setRegion] = useState(prefill?.region || 'us');
  const [performanceStatus, setPerformanceStatus] = useState('unknown');
  const [priorModalities, setPriorModalities] = useState([]);
  const [biomarkers, setBiomarkers] = useState([]);
  const [suggestedMarkers, setSuggestedMarkers] = useState([]);
  const [customMarker, setCustomMarker] = useState('');
  const [cancers, setCancers] = useState([
    { id: 'Breast Cancer', label: 'Breast Cancer' },
    { id: 'Lung Cancer', label: 'Lung Cancer' },
    { id: 'Colorectal Cancer', label: 'Colorectal Cancer' },
    { id: 'Prostate Cancer', label: 'Prostate Cancer' },
    { id: 'Pancreatic Cancer', label: 'Pancreatic Cancer' },
    { id: 'Ovarian Cancer', label: 'Ovarian Cancer' },
    { id: 'Leukemia', label: 'Leukemia' },
    { id: 'Non-Hodgkin Lymphoma', label: 'Non-Hodgkin Lymphoma' },
    { id: 'Oral Cancer', label: 'Oral Cancer' },
    { id: 'Cervical Cancer', label: 'Cervical Cancer' },
    { id: 'Liver Cancer', label: 'Liver Cancer' },
    { id: 'Melanoma', label: 'Melanoma' },
    { id: 'Brain Tumors (High-Grade Gliomas)', label: 'Brain Tumors (High-Grade Gliomas)' },
  ]);
  const [result, setResult] = useState(EMPTY_RESULT);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [profileBanner, setProfileBanner] = useState(Boolean(prefill?.cancerType));
  const [error, setError] = useState('');
  const resultsRef = useRef(null);
  const requestSeq = useRef(0);

  useEffect(() => {
    let alive = true;
    fetchCatalog()
      .then((data) => {
        if (!alive || !data?.cancers?.length) return;
        setCancers(data.cancers.map((c) => ({ id: c.id || c.label, label: c.label || c.id })));
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    fetchSuggestedBiomarkers(cancerType)
      .then((markers) => {
        if (alive) setSuggestedMarkers(markers || []);
      })
      .catch(() => {
        if (alive) setSuggestedMarkers([]);
      });
    return () => {
      alive = false;
    };
  }, [cancerType]);

  const runPersonalize = useCallback(async (scroll = true) => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setError('');
    try {
      const payload = {
        cancer_type: cancerType,
        stage,
        goals,
        biomarkers,
        line_of_care: lineOfCare,
        region,
        performance_status: performanceStatus,
        prior_modalities: priorModalities,
      };
      const data = await personalizeTreatmentOptions(payload);
      if (seq !== requestSeq.current) return;
      setResult(data);
      if (scroll) {
        requestAnimationFrame(() => {
          resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setError('Something went wrong building your option themes. Please try again.');
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [cancerType, stage, goals, biomarkers, lineOfCare, region, performanceStatus, priorModalities]);

  // Auto-personalize on first paint and when core inputs settle (debounced).
  useEffect(() => {
    const t = setTimeout(() => {
      runPersonalize(false);
    }, 280);
    return () => clearTimeout(t);
  }, [runPersonalize]);

  const toggleGoal = (id) => {
    setGoals((prev) => (prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id]));
  };

  const togglePrior = (id) => {
    setPriorModalities((prev) => (prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id]));
  };

  const toggleMarker = (marker) => {
    setBiomarkers((prev) =>
      prev.includes(marker) ? prev.filter((m) => m !== marker) : [...prev, marker]
    );
  };

  const addCustomMarker = (e) => {
    e.preventDefault();
    const value = customMarker.trim();
    if (!value) return;
    setBiomarkers((prev) => (prev.includes(value) ? prev : [...prev, value]));
    setCustomMarker('');
  };

  const copyBrief = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(buildClinicBriefText(result));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Could not copy to clipboard on this device.');
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    runPersonalize(true);
  };

  const strongThemes = result?.themes?.filter((t) => t.fit === 'strong') || [];

  return (
    <div className="care-page to-page min-h-screen bg-cream-paper">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-8 sm:pt-10 pb-16">
        <CarePageHeader
          current="/treatment-options"
          title="Personalized treatment options"
          lede="Case-aware educational themes by cancer type, stage, biomarkers, and goals, built to prepare clinic questions, not prescribe a regimen."
        />

        {profileBanner && (
          <div className="to-banner" role="status">
            <Sparkles className="w-4 h-4 shrink-0" aria-hidden />
            <p>
              Prefilling from your saved care profile. Adjust anything that has changed.
            </p>
            <button type="button" onClick={() => setProfileBanner(false)} aria-label="Dismiss">
              Dismiss
            </button>
          </div>
        )}

        <form className="to-sheet" onSubmit={onSubmit}>
          <div className="to-grid to-grid-3">
            <label className="to-field">
              <span>Cancer type</span>
              <select value={cancerType} onChange={(e) => setCancerType(e.target.value)}>
                {cancers.map((row) => (
                  <option key={row.id} value={row.id}>{row.label}</option>
                ))}
              </select>
            </label>
            <label className="to-field">
              <span>Stage band</span>
              <select value={stage} onChange={(e) => setStage(e.target.value)}>
                {STAGES.map((row) => (
                  <option key={row.id} value={row.id}>{row.label}</option>
                ))}
              </select>
            </label>
            <label className="to-field">
              <span>Where you are in care</span>
              <select value={lineOfCare} onChange={(e) => setLineOfCare(e.target.value)}>
                {LINES_OF_CARE.map((row) => (
                  <option key={row.id} value={row.id}>{row.label}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="to-grid">
            <label className="to-field">
              <span>Care region</span>
              <select value={region} onChange={(e) => setRegion(e.target.value)}>
                {REGIONS.map((row) => (
                  <option key={row.id} value={row.id}>{row.label}</option>
                ))}
              </select>
            </label>
            <label className="to-field">
              <span>Day-to-day fitness</span>
              <select value={performanceStatus} onChange={(e) => setPerformanceStatus(e.target.value)}>
                {PERFORMANCE.map((row) => (
                  <option key={row.id} value={row.id}>{row.label}</option>
                ))}
              </select>
            </label>
          </div>

          <fieldset className="to-goals">
            <legend>What matters most right now</legend>
            <div className="to-goal-list">
              {GOALS.map((goal) => (
                <label key={goal.id} className={goals.includes(goal.id) ? 'is-on' : ''}>
                  <input
                    type="checkbox"
                    checked={goals.includes(goal.id)}
                    onChange={() => toggleGoal(goal.id)}
                  />
                  <span>{goal.label}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <fieldset className="to-goals">
            <legend>Treatments already received (optional)</legend>
            <div className="to-chip-row">
              {PRIOR_MODALITIES.map((row) => (
                <button
                  key={row.id}
                  type="button"
                  className={priorModalities.includes(row.id) ? 'is-on' : ''}
                  onClick={() => togglePrior(row.id)}
                >
                  {row.label}
                </button>
              ))}
            </div>
          </fieldset>

          <fieldset className="to-goals">
            <legend>Biomarkers / molecular results (optional)</legend>
            <p className="to-hint">
              Select known results. These elevate precision and systemic themes, they do not invent a drug list.
            </p>
            {suggestedMarkers.length > 0 && (
              <div className="to-chip-row">
                {suggestedMarkers.map((marker) => (
                  <button
                    key={marker}
                    type="button"
                    className={biomarkers.includes(marker) ? 'is-on' : ''}
                    onClick={() => toggleMarker(marker)}
                  >
                    {marker}
                  </button>
                ))}
              </div>
            )}
            <div className="to-marker-add">
              <input
                type="text"
                value={customMarker}
                onChange={(e) => setCustomMarker(e.target.value)}
                placeholder="Add another marker (e.g. EGFR exon 19)"
                aria-label="Add biomarker"
              />
              <Button type="button" variant="outline" className="rounded-full" onClick={addCustomMarker}>
                Add
              </Button>
            </div>
            {biomarkers.length > 0 && (
              <div className="to-selected-markers">
                {biomarkers.map((m) => (
                  <button key={m} type="button" onClick={() => toggleMarker(m)} title="Remove">
                    {m} ×
                  </button>
                ))}
              </div>
            )}
          </fieldset>

          <div className="to-actions">
            <Button
              type="submit"
              disabled={loading}
              className="h-12 px-6 rounded-full bg-forest-ink text-cream-paper hover:bg-forest-shadow inline-flex items-center"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
              {loading ? 'Personalizing…' : 'Update my option themes'}
            </Button>
            {result?._source && (
              <span className="to-source">
                {result._source === 'api' ? 'Live catalog personalization' : 'On-device fallback'}
              </span>
            )}
          </div>
          {error && <p className="to-error" role="alert">{error}</p>}
        </form>

        <section className="to-results" ref={resultsRef} aria-live="polite">
          {!result && loading && (
            <p className="to-loading">Building personalized themes…</p>
          )}

          {result && (
            <>
              <div className="to-results-head">
                <BookOpen className="w-4 h-4 shrink-0 mt-1" aria-hidden />
                <div>
                  <h2>Option themes for {result.cancer_label}</h2>
                  <p>
                    Ordered for a {STAGES.find((s) => s.id === result.stage)?.label || result.stage} conversation
                    {strongThemes.length ? ` · ${strongThemes.length} strong-fit theme${strongThemes.length > 1 ? 's' : ''}` : ''}.
                    Themes update as you change inputs, so you can explore common pathways before clinic.
                  </p>
                </div>
              </div>

              {result.missing_info?.length > 0 && (
                <div className="to-missing">
                  <h3>Strengthen this personalization</h3>
                  <ul>
                    {result.missing_info.map((item) => (
                      <li key={item.field}>
                        <span className={`to-priority is-${item.priority}`}>{item.priority}</span>
                        {item.why}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="to-cards">
                {result.themes.map((theme) => (
                  <article key={theme.id} className={`to-card is-${theme.fit}`}>
                    <div className="to-card-top">
                      <p className="to-rank">Theme {theme.rank}</p>
                      <span className="to-fit">{theme.fit} fit</span>
                    </div>
                    <h3>{theme.title}</h3>
                    <p>{theme.summary}</p>
                    <p className="to-why"><strong>Why ranked here:</strong> {theme.why_ranked}</p>
                    <p className="to-next"><strong>Clinic tip:</strong> {theme.clinic_tip}</p>
                  </article>
                ))}
              </div>

              {result.cancer_specific_options?.length > 0 && (
                <section className="to-panel">
                  <div className="to-panel-head">
                    <ClipboardList className="w-4 h-4" aria-hidden />
                    <h3>Cancer-specific options to discuss</h3>
                  </div>
                  <p className="to-panel-lede">
                    Curated educational bullets for {result.cancer_label}. Confirm relevance with your oncologist.
                  </p>
                  <ul className="to-bullets">
                    {result.cancer_specific_options.map((opt, idx) => (
                      <li key={`${idx}-${opt.text.slice(0, 24)}`}>
                        {opt.text}
                        {opt.modality_hint && <em>{opt.modality_hint}</em>}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {(result.biomarker_notes?.length > 0 || result.molecular_markers?.length > 0) && (
                <section className="to-panel">
                  <div className="to-panel-head">
                    <FlaskConical className="w-4 h-4" aria-hidden />
                    <h3>Biomarkers & precision notes</h3>
                  </div>
                  {result.molecular_markers?.length > 0 && (
                    <p className="to-panel-lede">
                      Common markers for this cancer family:{' '}
                      {result.molecular_markers.join(' · ')}
                    </p>
                  )}
                  {result.biomarker_notes?.length > 0 && (
                    <ul className="to-bullets">
                      {result.biomarker_notes.map((note) => (
                        <li key={note.marker}>
                          <strong>{note.marker}:</strong> {note.note}
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              )}

              {result.india_context && (
                <section className="to-panel to-panel-soft">
                  <h3>India care context</h3>
                  <p>{result.india_context}</p>
                </section>
              )}

              <div className="to-two">
                <section className="to-panel">
                  <div className="to-panel-head">
                    <HelpCircle className="w-4 h-4" aria-hidden />
                    <h3>Questions for your clinic visit</h3>
                  </div>
                  <ol className="to-numbered">
                    {result.clinic_questions.map((q) => (
                      <li key={q}>{q}</li>
                    ))}
                  </ol>
                  <Button
                    type="button"
                    variant="outline"
                    className="mt-4 rounded-full"
                    onClick={copyBrief}
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    {copied ? 'Copied clinic brief' : 'Copy clinic brief'}
                  </Button>
                </section>

                <section className="to-panel">
                  <div className="to-panel-head">
                    <CheckCircle2 className="w-4 h-4" aria-hidden />
                    <h3>Shared decision checklist</h3>
                  </div>
                  <ul className="to-bullets">
                    {result.shared_decision_checklist.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </section>
              </div>

              <div className="to-two">
                <section className="to-panel">
                  <div className="to-panel-head">
                    <Users className="w-4 h-4" aria-hidden />
                    <h3>Multidisciplinary care</h3>
                  </div>
                  <p>{result.multidisciplinary_note}</p>
                  <p className="to-panel-lede mt-3">{result.clinical_trials_note}</p>
                </section>
                <section className="to-panel">
                  <h3>Side effects & quality of life</h3>
                  <ul className="to-bullets">
                    {result.side_effect_priorities.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </section>
              </div>

              <div className="to-ctas">
                <p>Take the next practical step</p>
                <div className="to-cta-row">
                  {(result.next_steps || []).map((step, idx) => (
                    <Link key={step.id} to={step.href} title={step.reason} className={idx === 0 ? 'is-primary' : ''}>
                      {step.label}
                    </Link>
                  ))}
                </div>
              </div>

              <p className="to-disclaimer">{result.disclaimer}</p>
              <p className="to-sources">{result.sources_note}</p>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
